import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Switch,
  useParams,
  Route,
  useRouteMatch,
  useHistory,
} from "react-router-dom";

import {
  Offcanvas,
  Row,
  Col,
  Button,
  Tab,
  Nav,
  Image,
  Form,
  InputGroup,
} from "react-bootstrap";
import dateformat from "dateformat";
import _ from "lodash";
import debounce from 'lodash.debounce';
import cx from "classnames";

import { FailureToast, SuccessToast } from "components/Toast";
import InfiniteScroll from "components/InfiniteScroll";
import ManageRoles from "./manageRoles";

import { toast } from "react-toastify";

import {
  fetchAllUsers,
  updateUserAccess,
  invalidateUserAccess,
  fetchAllInvitations,
  inviteUser,
  invalidateInviteUser,
  resendInvitation,
  deleteInvitation,
  updateInvitationRole,
  invalidateUsers
} from "actions/userManagement";
import { fetchUserRoles } from "actions/userRoles";
import { MIXPANEL_PAGE_VIEWED, MIXPANEL_PAGE_VIEWED_WORKSPACE_USER_MANAGEMENT, MIXPANEL_USER_MANAGEMENT_SEARCH } from "constants/mixpanelEvents";
import { createMixpanelEvent } from "actions/mixpanel";
import { getPageViewedEventPayload } from "services/mixpanelUtil";

import styles from "./usermanagement.module.scss";

/**
 * User Management Components
 */
export default function UserManagementView(props) {
  const dispatch = useDispatch();
  const { id } = useParams();
  let { path, url } = useRouteMatch();
  let history = useHistory();

  const [state, setState] = useState({
    inviteUser: false,
  });

  const toggleInviteUserClick = () => {
    setState((state) => {
      return {
        ...state,
        inviteUser: !state.inviteUser,
      };
    });
  };

  useEffect(() => {
    dispatch(fetchUserRoles());

    var mixPanelPayload = getPageViewedEventPayload(MIXPANEL_PAGE_VIEWED_WORKSPACE_USER_MANAGEMENT)
    dispatch(createMixpanelEvent(MIXPANEL_PAGE_VIEWED, mixPanelPayload))

    return () => {
      dispatch(invalidateUsers());
    }
  }, []);

  return (
    <div className={styles.content}>
      <header className={styles.header}>
        <div className={styles.wrapper}>
          <h1>User Management</h1>
          <span>Choose who you’ll share this space with (Get it, get it?)</span>
        </div>

        <Button
          type="submit"
          variant="primary"
          onClick={toggleInviteUserClick}
          data-track-event="Mixpanel" data-track-props="Invite User"
        >
          <i className={cx(styles.icon, 'cleverstory-plus')} />
          Invite User
        </Button>
      </header>

      <Switch>
        <Route exact path={`${path}`}>
          <UserManagement inviteUser={state.inviteUser} toggleInviteUserClick={toggleInviteUserClick} />
        </Route>

        <Route exact path={`${path}/manage-roles`}>
          <ManageRoles />
        </Route>

        <Route path={`${path}/manage-roles/:id`}>
          <ManageRoles />
        </Route>
      </Switch>

      <InviteUserView show={state.inviteUser} onClose={toggleInviteUserClick} />
    </div>
  );
}

function UserManagement({ inviteUser, toggleInviteUserClick }) {

  return (
    <div className={styles.userManagement}>
      <Tab.Container id="user-management-tab" defaultActiveKey="users">
        <header className={styles.userManagementHeader}>
          <Nav variant="pills" className={styles.userManagementTab}>
            <Nav.Item>
              <Nav.Link eventKey="users">Users</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="invitations">Invitations</Nav.Link>
            </Nav.Item>
          </Nav>

          <section
            className={cx(styles.buttonContainer, styles.primaryActions)}
          >
            {/* <Button className={styles.saveButton} type="submit" onClick={toggleInviteUserClick}>Manage Roles</Button> */}
          </section>
        </header>

        <Tab.Content>
          <Tab.Pane eventKey="users">
            <UserListView InviteUser={toggleInviteUserClick} />
          </Tab.Pane>
          <Tab.Pane eventKey="invitations">
            <InvitationListView InviteUser={toggleInviteUserClick} />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
}

/**
 * User UI Components
 */
function UserListView(props) {
  const dispatch = useDispatch();
  const { id } = useParams();

  const [editUser, setEditUser] = useState(null);
  const [userActiveToggle, setUserActiveToggle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused]=useState(false);
  const users = useSelector((state) =>
    _.get(state, "userManagement.users.items", [])
  );
  const loading = useSelector((state) =>
    _.get(state, "userManagement.users.loading", false)
  );
  const error = useSelector((state) =>
    _.get(state, "userManagement.users.error", false)
  );
  const pagination = useSelector((state) =>
    _.get(state, "userManagement.users.pagination")
  );
  const hasMoreItems = _.get(pagination, "current") < _.get(pagination, "totalPages") - 1;

  useEffect(() => {
    handleSearch(searchQuery.trimStart());
  }, [searchQuery]);

  const handleSearch = React.useCallback(
    _.debounce((searchQuery) => {
      dispatch(fetchAllUsers(id, 0, searchQuery));
    }, 300),
    []
  );

  const loadMoreUsers = () => {
    const nextPage = pagination.current + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }

    if(!error) {
      dispatch(fetchAllUsers(id, nextPage, searchQuery.trimStart()));
    }
  };

  const handleEditUser = (user) => {
    setEditUser(user);
  };

  const handleToggleUser = (user) => {
    setUserActiveToggle(user);
  };
  useEffect(() => {
    if (!searchQuery) return;
    
    const timeout = setTimeout(() => {
      dispatch(createMixpanelEvent(MIXPANEL_USER_MANAGEMENT_SEARCH));
    }, 2500);
  
    return () => clearTimeout(timeout);
  }, [searchQuery]);
  

  const handleInputChange = (e) => {
    setIsFocused(true)
    setSearchQuery(e.target.value);
  }

  if (loading && users.length === 0 && !isFocused) {
    return (
      <div className={styles.loaderContainer}>
        <label>Just a moon-ute... Scanning for life forms</label>
      </div>
    )
  }

  return (
    <>
      <aside>
          <InputGroup className={styles.searchBox}>
            <InputGroup.Text className={styles.searchIcon} id="basic-addon2">
              <i className="cleverstory-search" />
            </InputGroup.Text>
            <Form.Control
              className={styles.searchInput}
              placeholder="Search"
              aria-label="Search"
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              value={searchQuery}
              onChange={handleInputChange}
              aria-describedby="basic-addon2"
            />
            <div className={cx(styles.loaderWrapper, {[styles.showLoader]: (loading && isFocused) })}>
              <i className={cx(styles.loadIcon, 'cleverstory-spinloader')} />
            </div>
          </InputGroup>
        </aside>
      {!loading && (users.length === 0 || error) &&
        <div className={styles.emptyMessageContainer}>
          <div className={styles.emptyMessage}>
            <Image className={styles.emptyImage} src="/images/emptyUser.png" />
            <div className={styles.errorMessageContent}>
              <div className={styles.errorMessageTitle}>
                <h2>No man<span>,</span><br /></h2>
                <h2>No mouse<span>,</span><br /></h2>
              </div>
              <div className={styles.errorMessageSubText}>
                <span>It's an empty<br />house.<br /></span>
              </div>
              <Button
                type="submit"
                variant="primary"
                className={styles.inviteButton}
                onClick={props.InviteUser}
              >
                Invite User
              </Button>
            </div>
          </div>
        </div>
      }
      {!error &&
        <InfiniteScroll
          className={styles.scrollable}
          loading={loading}
          hasMore={hasMoreItems}
          error={error}
          loadMore={() => {
            loadMoreUsers();
          }}
        >
          {
            users.map((item) => {
              return (
                <UserRow
                  key={item.id}
                  user={item}
                  onEdit={handleEditUser}
                  onToggle={handleToggleUser}
                />
              );
            })
          }
        </InfiniteScroll>
      }

      <ManageUserRole
        show={editUser != null}
        onClose={() => {
          setEditUser(null);
          dispatch(invalidateUserAccess());
        }}
        user={editUser}
      />
      <ToggleUserActivationStatus
        show={userActiveToggle != null}
        onClose={() => {
          setUserActiveToggle(null);
          dispatch(invalidateUserAccess());
        }}
        user={userActiveToggle}
      />
    </>
  );
}

function UserRow({ user, onEdit, onToggle }) {
  const currentUser = useSelector((state) => _.get(state, "users.me"));
  const canEdit = currentUser && currentUser.id != user.id;

  return (
    <Row className={styles.userRow}>
      <Col md="4">
        <div className={styles.userDetails}>
          <div className={styles.iconContainer}>
            <div
              className={cx(styles.contactAvatar, {
                [styles.hasIcon]: user.icon,
              })}
            >
              {!user.icon && renderInitials(user)}{" "}
            </div>
            {user.icon && (
              <Image
                className={styles.contactIcon}
                src={user.icon.thumbnail}
                roundedCircle
              />
            )}
          </div>
          <div className={styles.userDetailsBody}>
            <h2 className={styles.userName}>
              {user.firstName} {user.lastName}
            </h2>
            <label className={styles.email}>{user.email}</label>
          </div>
        </div>
      </Col>
      <Col md="2" className={styles.userColumn}>
        <div className={styles.rolesInfo}>
          {_.get(user, "roles.0.name", "")}
        </div>
      </Col>
      <Col md="2" className={styles.userColumn}>
        <div className={styles.lastActiveDate}>
          <label className={styles.lastActiveLabel}>Last Active</label>
          <span className={styles.lastActiveDate}>
            On {dateformat(user.lastLoginDate, "dd mmm yyyy")}
          </span>
        </div>
      </Col>
      <Col md="2" className={styles.userColumn}>
        {user.enabled && canEdit && (
          <Button
            variant="light"
            className={styles.userRowButton}
            onClick={() => onEdit(user)}
          >
            Change Role
          </Button>
        )}
      </Col>
      <Col md="2" className={cx(styles.userColumn, "justify-content-end")}>
        {canEdit && (
          <Button
            variant="light"
            className={styles.userRowButton}
            onClick={() => onToggle(user)}
          >
            {user.enabled ? "Deactivate" : "Activate"}
          </Button>
        )}
      </Col>
    </Row>
  );
}

function ManageUserRole({ show, onClose, user }) {
  const dispatch = useDispatch();

  const workspace = useSelector((state) =>
    _.get(state, "workspaces.workspace.item.id")
  );
  const userRoles = useSelector((state) => _.get(state, "userRoles.items", []));
  const updateState = useSelector((state) =>
    _.get(state, "userManagement.users.user.update")
  );

  const [state, setState] = useState({
    selectedRole: null,
  });

  useEffect(() => {
    if (user) {
      setState((state) => {
        return {
          ...state,
          selectedRole: _.get(user, "roles.0.id"),
        };
      });
    }
  }, [user]);

  useEffect(() => {
    if (updateState && user) {
      if (updateState.success) {
        toast(
          <SuccessToast
            heading={`Success`}
            description={`User ${user.email} updated successfully`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );

        onClose();
      }
    }
  }, [updateState]);

  const handleSelectUserRole = (role) => {
    setState((state) => {
      return {
        ...state,
        selectedRole: role,
      };
    });
  };

  const handleChangeRole = () => {
    let request = {
      userRoles: [state.selectedRole],
    };

    dispatch(updateUserAccess(workspace, user.id, request));
  };

  return (
    <Offcanvas className={styles.manageRole} show={show} backdrop={true}>
      {user && (
        <>
          <h2 className={styles.heading}>Modify User Role</h2>
          <div className={styles.summary}>for {user.email}</div>
          {userRoles.map((role) => {
            return (
              <UserRoleRow
                key={role.id}
                userRole={role}
                selected={role.id === state.selectedRole}
                onChange={handleSelectUserRole}
              />
            );
          })}

          <footer
            className={cx(
              styles.inviteButtonContainer,
              styles.manageRoleButtonContainer
            )}
          >
            <Button
              variant="secondary"
              type="reset"
              onClick={() => {
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              onClick={handleChangeRole}
            >
              {_.get(updateState, "updating") ? "Saving..." : "Save Changes"}
            </Button>
          </footer>
        </>
      )}
    </Offcanvas>
  );
}

function ToggleUserActivationStatus({ show, onClose, user }) {
  const dispatch = useDispatch();

  const workspace = useSelector((state) =>
    _.get(state, "workspaces.workspace.item.id")
  );

  const updateState = useSelector((state) =>
    _.get(state, "userManagement.users.user.update")
  );

  const toggleUserActivation = () => {
    let request = {
      enabled: !user.enabled,
    };

    dispatch(updateUserAccess(workspace, user.id, request));
  };

  useEffect(() => {
    if (updateState && user) {
      if (updateState.success) {
        toast(
          <SuccessToast
            heading={`Success`}
            description={`User ${user.email} updated successfully`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );

        onClose();
      }
    }
  }, [updateState]);

  return (
    <Offcanvas
      placement="end"
      className={styles.confirmationDialog}
      show={show}
      backdrop={true}
    >
      {user && (
        <>
          <h1 className={styles.heading}>Toggle User Activation</h1>
          <div className={styles.summary}>
            <span>for {user.email}</span> <br />
            <span>Are you sure you want to change the user status?</span>
          </div>

          <footer className={styles.actionContainer}>
            <Button
              variant="primary"
              type="submit"
              onClick={toggleUserActivation}
            >
              {_.get(updateState, "updating") && user.enabled ? "Deactivating..." :
                _.get(updateState, "updating") && !user.enabled ? "Activating..." :
                  user.enabled ? "Deactivate" : "Activate"}
            </Button>

            <Button
              variant="secondary"
              type="reset"
              onClick={() => {
                onClose();
              }}
            >
              Cancel
            </Button>
          </footer>
        </>
      )}
    </Offcanvas>
  );
}

/**
 * Invitation UI Components
 */
function InvitationListView(props) {
  const dispatch = useDispatch();
  const { id } = useParams();

  const [query, setQuery] = useState("");
  const [editInvitation, setEditInvitation] = useState(null);
  const [deleteInvitation, setDeleteInvitation] = useState(null);

  const invitations = useSelector((state) =>
    _.get(state, "userManagement.invitations.items", [])
  );
  const loading = useSelector((state) =>
    _.get(state, "userManagement.invitations.loading", false)
  );
  const pagination = useSelector((state) =>
    _.get(state, "userManagement.invitations.pagination")
  );
  const hasMoreItems =
    _.get(pagination, "current") < _.get(pagination, "totalPages") - 1;

  const resend = useSelector((state) =>
    _.get(state, "userManagement.invitations.invitation.resend")
  );

  useEffect(() => {
    handleSearch(query);
  }, [query]);

  useEffect(() => {
    if (resend && resend.success) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`Invitation email sent successfully`}
        />,
        {
          toastId: 'success',
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );
      dispatch(invalidateInviteUser());
    }
  }, [resend]);

  const loadMoreUsers = () => {
    const nextPage = pagination.current + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }

    dispatch(fetchAllInvitations(id, nextPage, query));
  };

  const handleSearch = (query) => {
    dispatch(fetchAllInvitations(id, 0, query));
  };

  const handleDeleteInvitation = (invitation) => {
    setDeleteInvitation(invitation);
  };

  if (loading && invitations.length === 0) {
    return (
      <div className={styles.loaderContainer}>
        <label>Just a moon-ute... Scanning for life forms</label>
      </div>
    )
  }

  return (
    <>
      {!loading && invitations.length === 0 &&
        <div className={styles.emptyMessageContainer}>
          <div className={styles.emptyMessage}>
            <Image className={styles.emptyImage} src="/images/emptyUser.png" />
            <div className={styles.errorMessageContent}>
              <div className={styles.errorMessageTitle}>
                <h2>No man<span>,</span><br /></h2>
                <h2>No mouse<span>,</span><br /></h2>
              </div>
              <div className={styles.errorMessageSubText}>
                <span>It's an empty<br />house.<br /></span>
              </div>
              <Button
                type="submit"
                variant="primary"
                className={styles.inviteButton}
                onClick={props.InviteUser}
              >
                Invite User
              </Button>
            </div>
          </div>
        </div>
      }
      <InfiniteScroll
        className={styles.scrollable}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={() => {
          loadMoreUsers();
        }}
      >
        {invitations.map((item) => {
          return (
            <InvitationRow
              key={item.id}
              invitation={item}
              onEdit={(invitation) => {
                setEditInvitation(invitation);
              }}
              onDelete={handleDeleteInvitation}
            />
          );
        })}
      </InfiniteScroll>
      <ManageInvitationRole
        show={editInvitation != null}
        onClose={() => {
          setEditInvitation(null);
          dispatch(invalidateInviteUser());
        }}
        invitation={editInvitation}
      />
      <DeleteInvitation
        show={deleteInvitation != null}
        onClose={() => {
          setDeleteInvitation(null);
          dispatch(invalidateInviteUser());
        }}
        invitation={deleteInvitation}
      />
    </>
  );
}

function InvitationRow({ invitation, onEdit, onDelete }) {
  const dispatch = useDispatch();
  const workspace = useSelector((state) =>
    _.get(state, "workspaces.workspace.item.id")
  );

  const renderRole = () => {
    var userRoles = _.get(invitation, "userRoles", []);

    if (userRoles && userRoles.length > 0) {
      return userRoles[0].name;
    }

    return null;
  };

  const handleResend = () => {
    dispatch(resendInvitation(workspace, invitation.id));
  };

  return (
    <Row className={styles.userRow}>
      <Col md="4">
        <div className={styles.userDetails}>
          <div className={styles.iconContainer}>
            <div className={styles.contactAvatar}>
              {" "}
              {renderInitials(invitation)}{" "}
            </div>
          </div>
          <div className={styles.userDetailsBody}>
            <h2 className={styles.userName}>{invitation.email}</h2>
          </div>
        </div>
      </Col>
      <Col md="2" className={styles.userColumn}>
        <div className={styles.rolesInfo}>{renderRole()}</div>
      </Col>
      <Col md="2" className={styles.userColumn}>
        <div className={styles.lastActiveDate}>
          <label className={styles.lastActiveLabel}>Invited on</label>
          <span className={styles.lastActiveDate}>
            {dateformat(invitation.invitedDate, "dd mmm yyyy")}
          </span>
        </div>
      </Col>
      <Col className={styles.userColumn}>
        <Button
          variant="light"
          className={styles.userRowButton}
          disabled={invitation.deleting}
          onClick={() => onEdit(invitation)}
        >
          Change Role
        </Button>
      </Col>
      <Col className={styles.userColumn}>
        <Button
          variant="light"
          className={styles.userRowButton}
          onClick={() => onDelete(invitation)}
        >
          Delete
        </Button>
      </Col>
      <Col className={styles.userColumn}>
        <Button
          variant="light"
          className={styles.userRowButton}
          disabled={invitation.resending}
          onClick={handleResend}
        >
          {invitation.resending ? "Resending..." : "Resend"}
        </Button>
      </Col>
    </Row>
  );
}

function InviteUserView({ show, onClose }) {
  const dispatch = useDispatch();
  const { id } = useParams();

  const userRoles = useSelector((state) => _.get(state, "userRoles.items", []));
  const inviting = useSelector((state) =>
    _.get(state, "userManagement.invitations.invitation.inviting", false)
  );
  const invitation = useSelector((state) =>
    _.get(state, "userManagement.invitations.invitation.item")
  );
  const errors = useSelector((state) =>
    _.get(state, "userManagement.invitations.invitation.error")
  );

  const [state, setState] = useState({
    email: "",
    selectedRole: null,
  });

  useEffect(() => {
    if (userRoles && userRoles.length > 0) {
      var selected = userRoles[0].id;
      setState((state) => {
        return {
          ...state,
          selectedRole: selected,
        };
      });
    }
  }, [userRoles]);

  useEffect(() => {
    if (show) {
      var userRole = userRoles.find((item) => {
        return item.key === "publisher";
      });

      setState((state) => {
        return {
          email: "",
          selectedRole: userRole ? userRole.id : null,
        };
      });
    }
  }, [show]);

  useEffect(() => {
    if (invitation) {
      dispatch(invalidateInviteUser());
      // onClose();
      toast(
        <SuccessToast
          heading={`Success`}
          description={`${invitation.email} has been invited successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );
    }
  }, [invitation]);

  useEffect(() => {
    if (errors && errors.length > 0) {
      errors.forEach((error) => {
        toast(
          <FailureToast
            heading="Failure"
            description={error.message}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
      });
      dispatch(invalidateInviteUser());
    }
  }, [errors]);

  useEffect(() => {
    setState((state) => {
      return {
        ...state,
        errors: errors,
      };
    });
  }, [errors]);

  const handleSelectUserRole = (role) => {
    setState((state) => {
      return {
        ...state,
        selectedRole: role,
      };
    });
  };

  const handleEmailChange = (e) => {
    setState((state) => {
      return {
        ...state,
        email: e.target.value,
      };
    });
  };

  const handleInviteUser = () => {
    var errors = validateForm();
    setState((state) => {
      return {
        ...state,
        errors: errors,
      };
    });

    if (Object.keys(errors).length !== 0) {
      return;
    }

    let request = {
      email: state.email,
      userRoles: [state.selectedRole],
    };

    dispatch(inviteUser(request));
    onClose();
  };

  const validateForm = () => {
    var errors = new Array();
    const { email, selectedRole } = state;

    if (!email || email.trim === "") {
      errors.push({
        message: "Enter a valid email id",
      });
    }

    const regex =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!regex.test(email)) {
      errors.push({
        message: "Enter a valid email id",
      });
    }

    if (!selectedRole || selectedRole.trim === "") {
      errors.push({
        message: "Select an user role",
      });
    }

    return errors;
  };

  return (
    <Offcanvas className={styles.inviteUserOffCanvas} show={show}>
      <div className={styles.inviteUser}>
        <div className={styles.inner}>
          <h1 className={styles.header}>Invite User to Workspace</h1>

          <Form className={styles.form}>
            <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
              <Form.Label>Enter email address here</Form.Label>
              <Form.Control
                type="email"
                placeholder="name@example.com"
                value={state.email}
                onChange={handleEmailChange}
              />
              {_.size(state, "errors") > 0 && (
                <Form.Label className={styles.error}>
                  {_.get(state, "errors.0.message", " ")}
                </Form.Label>
              )}
            </Form.Group>
          </Form>

          <UserRoles
            userRoles={userRoles}
            selected={state.selectedRole}
            onChange={handleSelectUserRole}
          />
        </div>
        <footer className={styles.inviteButtonContainer}>
          <Button
            variant="secondary"
            type="reset"
            className={styles.actionButton}
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={inviting}
            type="submit"
            onClick={handleInviteUser}
          >
            {inviting ? "Inviting..." : "Invite User now"}
          </Button>
        </footer>
      </div>
    </Offcanvas>
  );
}

function ManageInvitationRole({ show, onClose, invitation }) {
  const dispatch = useDispatch();

  const workspace = useSelector((state) =>
    _.get(state, "workspaces.workspace.item.id")
  );
  const userRoles = useSelector((state) => _.get(state, "userRoles.items", []));
  const updateState = useSelector((state) =>
    _.get(state, "userManagement.invitations.invitation.update")
  );

  const [state, setState] = useState({
    selectedRole: null,
  });

  useEffect(() => {
    if (invitation && invitation.userRoles) {
      var userRole = invitation.userRoles[0] || {};
      setState((state) => {
        return {
          ...state,
          selectedRole: userRole ? userRole.id : null,
        };
      });
    }
  }, [invitation]);

  useEffect(() => {
    if (updateState) {
      if (updateState.success) {
        handleClose();
        toast(
          <SuccessToast
            heading={`Success`}
            description={`Role for ${invitation.email} has been modified successfully`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
      }
    }
  }, [updateState]);

  const handleSelectUserRole = (role) => {
    setState((state) => {
      return {
        ...state,
        selectedRole: role,
      };
    });
  };

  const handleChangeRole = () => {
    let request = {
      userRoles: [state.selectedRole],
    };

    dispatch(updateInvitationRole(workspace, invitation.id, request));
  };

  const handleClose = () => {
    dispatch(invalidateInviteUser());
    onClose();
  };

  return (
    <Offcanvas className={styles.manageRole} show={show} backdrop={true}>
      {invitation && (
        <>
          <h2 className={styles.heading}>Modify Invitation Role</h2>
          <div className={styles.summary}>for {invitation.email}</div>

          {userRoles.map((role) => {
            return (
              <UserRoleRow
                key={role.id}
                userRole={role}
                selected={role.id === state.selectedRole}
                onChange={handleSelectUserRole}
              />
            );
          })}

          <footer
            className={cx(
              styles.inviteButtonContainer,
              styles.manageRoleButtonContainer
            )}
          >
            <Button
              variant="secondary"
              type="reset"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleChangeRole}
            >
              {invitation.updatingAccess ? "Saving..." : "Save Changes"}
            </Button>
          </footer>
        </>
      )}
    </Offcanvas>
  );
}

function DeleteInvitation({ show, onClose, invitation }) {
  const dispatch = useDispatch();

  const workspace = useSelector((state) =>
    _.get(state, "workspaces.workspace.item.id")
  );

  const deletion = useSelector((state) =>
    _.get(state, "userManagement.invitations.invitation.deletion")
  );

  const handleDeleteInvitationClick = () => {
    dispatch(deleteInvitation(workspace, invitation.id));
  };

  useEffect(() => {
    if (deletion) {
      if (deletion.success) {
        toast(
          <SuccessToast
            heading={`Success`}
            description={`Deleted invitation successfully`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
        onClose();
      }
    }
  }, [deletion]);

  return (
    <Offcanvas
      placement="end"
      className={styles.confirmationDialog}
      show={show}
      backdrop={true}
    >
      {invitation && (
        <>
          <h1 className={styles.heading}>Delete Invitation</h1>
          <div className={styles.summary}>
            <span>for {invitation.email}</span> <br />
            <span>Are you sure you want to delete this invitation?</span>
          </div>

          <footer className={styles.actionContainer}>
            <Button
              type="submit"
              onClick={handleDeleteInvitationClick}
            >
              {invitation.deleting ? "Deleting..." : "Delete"}
            </Button>

            <Button
              type="reset"
              variant="secondary"
              onClick={() => {
                onClose();
              }}
            >
              Cancel
            </Button>
          </footer>
        </>
      )}
    </Offcanvas>
  );
}

/**
 * Common UI Components
 */
function UserRoles({ userRoles, selected, onChange }) {
  return (
    <div className={styles.userRoles}>
      <header className={styles.rolesHeader}>
        <h1 className={styles.rolesTitle}>Roles</h1>
        <p className={styles.rolesSubTitle}>
          Select a role for the person you’re inviting
        </p>
      </header>
      {userRoles.map((role) => {
        return (
          <UserRoleRow
            key={role.id}
            userRole={role}
            selected={role.id === selected}
            onChange={onChange}
          />
        );
      })}
    </div>
  )
}

function UserRoleRow({ userRole, selected, onChange }) {
  const handleSelectRole = () => {
    onChange(userRole.id);
  };

  return (
    <section className={styles.userRoleRow} onClick={handleSelectRole}>
      <aside className={styles.userRoleSelection}>
        <input
          className={styles.actionSelection}
          name="userRole"
          type="radio"
          checked={selected}
          onChange={handleSelectRole}
        />
        <span className={styles.checkmark}></span>
      </aside>
      <aside className={styles.userRowDetail}>
        <h1 className={styles.userRoleName}>{userRole.name}</h1>
        <p className={styles.userRoleDescription}>{userRole.description}</p>
      </aside>
    </section>
  );
}

function renderInitials(user) {
  let initial = "";

  if (user.name) {
    initial = user.name.charAt(0);
  } else {
    initial = user.email.charAt(0);
  }

  return initial.toUpperCase();
}